import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject, concat, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, last, map, tap } from 'rxjs/operators';
import { ExtensionsApiService } from '../api/extensions.api.service';
import { OrderProtectionProductInfo } from '@pages/checkout-app/checkout-layout/main/components/extensions/components/extension-components/order-protection/model';
import {
  CheckoutContentExtension,
  ExtensionComponentType,
  GiftUnlockExtension,
  ICheckoutExtensions,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { ToastService } from '@storybook/services/toast/toast.service';
import { capitalize } from '@utils/string-utils';
import { CheckoutApiService } from '../api/checkout.api.service';
import { ToastOptions } from '@app/storybook/services/toast/toast-types';
import { HistoricalPaymentInfo } from '@pdqdev/pdq-shared/dist/entities';
import { DynamicRule } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { SelectableProduct } from '@app/sharedModule/product-selection/components/add-products-modal/add-products-modal.component';
import { ShopifyDiscountNode } from './shopify-discount.model';

export interface CheckoutComponentMetric {
  METRIC_NAME: string;
  METRIC_TOTAL_VALUE: number;
  LATEST_SNAPSHOT_DATE: string;
}

export interface ProductSellingPlanOption {
  planId: string;
  planName: string;
  groupName?: string;
  interval?: string;
  intervalCount?: number;
}

export interface PreviewFlagResponse {
  previewFlag: string;
  expiresAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class ExtensionsService {
  public formChangesSubject = new Subject<any>();
  private extensionRequirements = new BehaviorSubject<{ type: ExtensionComponentType; status: string }[]>(null);
  readonly extensionRequirements$ = this.extensionRequirements.asObservable();
  dynamicFieldsAutocomplete$ = new BehaviorSubject<any>(null);
  private saveSubject = new Subject<void>();
  readonly save$ = this.saveSubject.asObservable();
  private formValidSubject = new BehaviorSubject<boolean>(true);
  readonly formValid$ = this.formValidSubject.asObservable();
  private currentExtensionTypeSubject = new BehaviorSubject<ExtensionComponentType | null>(null);
  readonly currentExtensionType$ = this.currentExtensionTypeSubject.asObservable();
  constructor(
    private extensionsApiService: ExtensionsApiService,
    private toastService: ToastService,
    private checkoutApiService: CheckoutApiService,
  ) {}

  validateExtensionsRequirements(): void {
    this.extensionsApiService
      .get('admin/v1/validate/requirements')
      .subscribe((response: { type: ExtensionComponentType; status: string }[]) => {
        this.extensionRequirements.next(response);
      });
  }

  loadExtensions(): Observable<ICheckoutExtensions[]> {
    return this.extensionsApiService.get('admin/v1/fetch/list').pipe(
      map((response: ICheckoutExtensions[]) => {
        return response;
      }),
      catchError((err) => {
        return throwError(err);
      }),
    );
  }

  loadExtensionsLightweight(): Observable<ICheckoutExtensions[]> {
    return this.extensionsApiService.get('admin/v1/fetch/list-lightweight').pipe(
      map((response: ICheckoutExtensions[]) => {
        return response;
      }),
      catchError((err) => {
        return throwError(err);
      }),
    );
  }

  loadNotLiveExtensionIds(extensionIds: number[]): Observable<number[]> {
    return this.extensionsApiService.post('admin/v1/fetch/not-live', { extensionIds }).pipe(
      map((response: number[]) => response ?? []),
      catchError((err) => {
        return throwError(err);
      }),
    );
  }

  loadExtensionById(id: number): Observable<ICheckoutExtensions> {
    return this.extensionsApiService.get(`admin/v1/${id}`).pipe(
      map((response: ICheckoutExtensions) => {
        return response;
      }),
      catchError((err) => {
        return throwError(err);
      }),
    );
  }

  createPreviewFlag(usageToken: string): Observable<PreviewFlagResponse> {
    return this.extensionsApiService.post('admin/v1/preview-flag', { usageToken });
  }

  resolveOrderProtectionProduct(): Observable<OrderProtectionProductInfo> {
    return this.extensionsApiService.get('admin/v1/order-protection/product');
  }

  createExtension(values): Observable<ICheckoutExtensions[]> {
    return this.extensionsApiService.post('admin/v1', values).pipe(
      map((response: ICheckoutExtensions[]) => {
        return response;
      }),
      catchError((err) => {
        return throwError(err);
      }),
    );
  }

  updateExtension(id: number, values): Observable<ICheckoutExtensions[]> {
    return this.extensionsApiService.patch(`admin/v1/${id}`, values).pipe(
      map((response: ICheckoutExtensions[]) => {
        return response;
      }),
      catchError((err) => {
        return throwError(err);
      }),
    );
  }

  uploadBadgeIcon(extensionId: number, badgeId: string, file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return this.extensionsApiService
      .patchMultipart(`admin/v1/${extensionId}/icon?badgeId=${encodeURIComponent(badgeId)}`, formData)
      .pipe(map((res: { icon: string }) => res.icon));
  }

  uploadPendingBadgeIcons<T extends Record<string, any>>(extensionId: number, template: T): Observable<T> {
    const groups = template?.['boostGroups'] as Array<{ badges?: any[] }> | undefined;
    if (!Array.isArray(groups)) return of(template);
    const pending: { badgeId: string; file: File; assign: (url: string) => void }[] = [];
    for (const group of groups) {
      for (const badge of group?.badges ?? []) {
        if (badge?.customIconUrl instanceof File && typeof badge?.id === 'string') {
          pending.push({
            badgeId: badge.id,
            file: badge.customIconUrl,
            assign: (url: string) => {
              badge.customIconUrl = url;
              if (badge.icon === '' || badge.icon == null || String(badge.icon).startsWith('blob:')) {
                badge.icon = url;
              }
            },
          });
        }
      }
    }
    if (!pending.length) return of(template);
    const uploads = pending.map((p) =>
      this.uploadBadgeIcon(extensionId, p.badgeId, p.file).pipe(tap((url) => p.assign(url))),
    );
    return concat(...uploads).pipe(
      last(),
      map(() => template),
    );
  }

  deleteExtension(id: number): Observable<ICheckoutExtensions[]> {
    return this.extensionsApiService.delete(`admin/v1/${id}`).pipe(
      map((response: ICheckoutExtensions[]) => {
        return response;
      }),
      catchError((err) => {
        return throwError(err);
      }),
    );
  }

  createToast(options?: ToastOptions): void {
    this.toastService.showToast(options);
  }

  loadMetrics(
    shopId: number,
    fromDate: string,
    toDate: string,
    extentionId: number,
    extensionType?: ExtensionComponentType,
  ): Observable<CheckoutComponentMetric[]> {
    const params = new HttpParams()
      .set('shop_id', shopId.toString())
      .set('from_date', fromDate)
      .set('to_date', toDate)
      .set('extension_id', extentionId.toString())
      .set('extension_type', extensionType);

    return this.extensionsApiService.get('admin/v1/metrics/data', { params }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err) => {
        return throwError(err);
      }),
    );
  }

  get dynamicFieldsAutocompleteValues(): string[] {
    return this.dynamicFieldsAutocomplete$.getValue();
  }

  initDynamicFieldsAutocomplete(): void {
    if (!this.dynamicFieldsAutocompleteValues) {
      const metafieldDefinitions$: Observable<
        { name: string; description: string; displayName: string; category: string; replaceableName?: string | null }[]
      > = this.checkoutApiService.get('admin/v1/shops/metafield-definitions/autocomplete');
      forkJoin([metafieldDefinitions$]).subscribe(
        ([metafieldDefinitions]) => {
          let options = [...CheckoutContentExtension.initialDynamicFieldsVariables, ...metafieldDefinitions].sort(
            (a, b) => a.name.localeCompare(b.name),
          );
          options = options.map((option) => {
            return {
              name: option.name,
              searchTerm: option.name.replace(/[{}]/g, ''),
              description: capitalize(option.description),
              displayName: option.displayName,
              category: option.category,
              replaceableName: option.replaceableName,
            };
          });
          this.dynamicFieldsAutocomplete$.next(options);
        },
        (error) => {
          console.error('Failed to init metafield definitions', error);
        },
      );
    }
  }
  triggerSave(): void {
    this.saveSubject.next();
  }

  setFormValid(valid: boolean): void {
    this.formValidSubject.next(valid);
  }
  getFormValid(): boolean {
    return this.formValidSubject.getValue();
  }
  setCurrentExtensionType(type: ExtensionComponentType | null): void {
    this.currentExtensionTypeSubject.next(type);
  }

  resetExtensionState(): void {
    this.formValidSubject.next(true);
    this.currentExtensionTypeSubject.next(null);
  }

  getHistoricalPaymentInfo(): Observable<HistoricalPaymentInfo | null> {
    return this.extensionsApiService.get('admin/v1/historical-payment-info');
  }

  loadDiscountCodes(search?: string): Observable<ShopifyDiscountNode[]> {
    const trimmed = search?.trim();
    const params = trimmed ? new HttpParams().set('search', trimmed) : undefined;
    return this.extensionsApiService.get('admin/v1/discount-codes', { params }).pipe(
      catchError((err) => {
        return throwError(err);
      }),
    );
  }

  getItemByProductOrVariantId(variantId: string): Observable<GiftUnlockExtension.OfferProduct | null> {
    return this.extensionsApiService.get(`admin/v1/item-by-product-or-variant-id/${variantId}`).pipe(
      map((response: { product?: GiftUnlockExtension.OfferProduct }) => response?.product ?? null),
      catchError((err) => {
        console.error('Failed to get product by variant id:', err);
        return throwError(() => err);
      }),
    );
  }

  queryShopifyForProductsUsingRestrictionRules(params: {
    query?: string;
    rules?: DynamicRule[];
    limit?: number;
  }): Observable<SelectableProduct[]> {
    const DEFAULT_PRODUCT_SEARCH_LIMIT = 50;
    const body = { ...params, limit: params.limit ?? DEFAULT_PRODUCT_SEARCH_LIMIT };
    return this.extensionsApiService.post('admin/v1/search-products-by-query', body).pipe(
      map((response: { products: SelectableProduct[] }) =>
        response.products.map((product: any) => {
          const firstVariant = product.variants?.edges?.[0]?.node;
          const firstImage = product.images?.nodes?.[0];

          const optionsMap = new Map<string, Set<string>>();
          product.variants?.edges?.forEach(({ node }) => {
            node.selectedOptions?.forEach(({ name, value }) => {
              if (!optionsMap.has(name)) optionsMap.set(name, new Set());
              optionsMap.get(name).add(value);
            });
          });
          const options = Array.from(optionsMap.entries()).map(([name, values]) => ({
            name,
            values: Array.from(values),
          }));

          const allPrices = product.variants?.edges?.map(({ node }) => node.price) ?? [];
          const hasUniformPrice = allPrices.length <= 1 || allPrices.every((p) => p === allPrices[0]);

          return {
            id: product.id,
            title: product.title,
            sku: firstVariant?.inventoryItem?.sku || firstVariant?.sku,
            price: firstVariant?.price,
            imageUrl: firstImage?.url,
            options,
            hasUniformPrice,
          };
        }),
      ),
      catchError((err) => {
        console.error('Failed to query products:', err);
        return throwError(err);
      }),
    );
  }

  getProductUpgradeSellingPlans(
    productIds: (string | number)[],
  ): Observable<Record<string, ProductSellingPlanOption[]>> {
    return this.extensionsApiService.post('admin/v1/product-upgrade/selling-plans-by-products', { productIds }).pipe(
      map(
        (response: { sellingPlansByProduct?: Record<string, ProductSellingPlanOption[]> }) =>
          response?.sellingPlansByProduct ?? {},
      ),
      catchError((err) => {
        console.error('Failed to fetch product-upgrade selling plans:', err);
        return throwError(() => err);
      }),
    );
  }

  getGiftUnlocksByOfferType(offerType: string): Observable<ICheckoutExtensions[]> {
    return this.extensionsApiService.get(`admin/v1/gift_unlock_extension/get_by_offer_type/${offerType}`).pipe(
      map((response: ICheckoutExtensions[]) => {
        return response;
      }),
      catchError((err) => {
        return throwError(err);
      }),
    );
  }
}
